import { useState, useEffect } from 'react'
import { Outlet, NavLink, Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../auth/AuthProvider'
import { useUserContext } from '../providers/UserContextProvider'
import { useLanguage } from '../hooks/useLanguage'
import { api } from '../api/client'
import { useUnreadNotificationCount } from '../features/stakeholder-status/useStakeholderNotifications'
import { usePermissions } from '../hooks/usePermissions'
import { ContextSwitcher } from './ui/ContextSwitcher'
import { pathAfterContextSwitch } from '../lib/contextSwitch'
import { useUnitSegment } from '../features/dashboard/UnitAddress'
import { FolioStrip } from './FolioStrip'
import { RoleHelpPanel } from '../features/onboarding/RoleHelpPanel'
import { FeedbackDialog } from '@edition/controls'
import { SAAS_ENABLED } from '@edition/ui'
import {
  LayoutDashboard,
  LogOut,
  Settings,
  Compass,
  Network,
  Newspaper,
  ClipboardCheck,
  HelpCircle,
  MessageSquarePlus,
  Smartphone,
  ArrowRight,
  ShieldCheck,
  Menu,
  X,
  type LucideIcon,
} from 'lucide-react'

type NavItem = { to: string; icon: LucideIcon; label: string; badge?: number }

const FEEDBACK_UPDATE_PATH = /^\/feedback\/updates\/([^/]+)\/?$/
const WORKSPACE_RETURN_KEY = 'okr-saas-workspace-return'

/**
 * Where an admin-only account is sent from a work page. A feedback answer's notification points at
 * the work route for its receipt, so it goes to the admin console's copy of that page and not to
 * the console's front page.
 */
function adminPathFor(pathname: string): string {
  const receipt = SAAS_ENABLED ? FEEDBACK_UPDATE_PATH.exec(pathname) : null
  return receipt ? `/admin/updates/${receipt[1]}` : '/admin'
}

export function Layout() {
  const { user, logout, hasRole } = useAuth()
  const { userDetail, primaryContext, hasOrgAdminAccess, workMemberships, isPureAdmin, setActiveMembership } = useUserContext()
  const { canApprove } = usePermissions()
  const { t } = useTranslation()
  const { language, changeLanguage } = useLanguage()
  const location = useLocation()
  const navigate = useNavigate()

  // Sync language from user preferences on mount
  useEffect(() => {
    if (userDetail?.preferredLanguage && userDetail.preferredLanguage !== language) {
      changeLanguage(userDetail.preferredLanguage)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetail?.preferredLanguage])

  useEffect(() => {
    if (location.pathname !== '/preferences') {
      sessionStorage.setItem(WORKSPACE_RETURN_KEY, `${location.pathname}${location.search}${location.hash}`)
    }
  }, [location.pathname, location.search, location.hash])

  const orgId = primaryContext?.organizationId
  // Work approvals belong to work-track managers; admins review access
  // requests in the admin console at /admin/approvals.
  const canSeeApprovals = canApprove
  const { data: pendingCountData } = useQuery({
    queryKey: ['approvals', 'count', orgId],
    queryFn: () => api.approvals.countPending(orgId!).then(r => r.data as { count: number }),
    enabled: !!orgId && canSeeApprovals,
    refetchInterval: 30_000,
  })
  const pendingCount = pendingCountData?.count ?? 0
  // Waits for /users/me, which links a first sign-in to its user row.
  const unreadStakeholderNotifications = useUnreadNotificationCount(!!userDetail)

  const isTeamMember = primaryContext?.entityType === 'PRODUCT_TEAM' && primaryContext?.productTeamId
  // The slug, as the team page's address bar shows it, so the item stays highlighted there.
  const teamSegment = useUnitSegment(isTeamMember ? primaryContext.productTeamId! : '')
  const commandCenterHref = isTeamMember
    ? `/teams/${teamSegment}`
    : '/'

  const businessNavItems: NavItem[] = [
    {
      to: commandCenterHref,
      icon: LayoutDashboard,
      label: t('nav.commandCenter'),
      badge: pendingCount > 0 ? pendingCount : undefined,
    },
    { to: '/strategy', icon: Compass, label: t('nav.strategy') },
    { to: '/directory', icon: Network, label: t('nav.directory') },
    { to: '/newsfeed', icon: Newspaper, label: t('nav.newsfeed'), badge: unreadStakeholderNotifications > 0 ? unreadStakeholderNotifications : undefined },
    ...(isTeamMember ? [{ to: '/check-in', icon: ClipboardCheck, label: t('nav.checkIn') }] : []),
  ]

  // Configuration lives in the admin console; the work nav links to it once.
  const adminNavItems: NavItem[] = hasOrgAdminAccess
    ? [{ to: '/admin', icon: ShieldCheck, label: t('nav.administration') }]
    : []

  const navItems = [...businessNavItems, ...adminNavItems]

  const displayName = userDetail?.fullName || user?.name || user?.email
  const userInitials = displayName
    ? displayName.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2)
    : user?.email?.[0]?.toUpperCase() || '?'

  const currentMembership = workMemberships.find((m) =>
    m.entityType === primaryContext?.entityType &&
    m.entityId === (
      primaryContext?.entityType === 'PRODUCT_TEAM' ? primaryContext?.productTeamId :
      primaryContext?.entityType === 'VALUE_STREAM' ? primaryContext?.valueStreamId :
      primaryContext?.entityType === 'DIVISION' ? primaryContext?.divisionId :
      // A level the tenant added has no named slot; its unit is the path's last node.
      primaryContext?.entityType === 'UNIT' ? primaryContext?.unit?.id :
      primaryContext?.organizationId
    )
  )

  // The page follows the chosen context, rather than leaving the old one's command centre on screen.
  function switchContext(membershipId: string) {
    if (membershipId === currentMembership?.id) return
    setActiveMembership(membershipId)
    const next = pathAfterContextSwitch(location.pathname, location.search)
    if (next) navigate(next)
  }

  const [showHelp, setShowHelp] = useState(false)
  const [showFeedback, setShowFeedback] = useState(false)
  // Platform super-admins read the feedback; they do not send it.
  const canSendFeedback = SAAS_ENABLED && !hasRole('super_admin')
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false)

  useEffect(() => {
    setIsMobileNavOpen(false)
  }, [location.pathname])

  // Pure admins (no work membership) work in the admin console. Account-level
  // pages like preferences remain reachable from there.
  if (isPureAdmin && userDetail && location.pathname !== '/preferences') {
    return <Navigate to={adminPathFor(location.pathname)} replace />
  }

  function renderNavItems(onNavigate?: () => void) {
    return navItems.map((item) => (
      <NavLink
        key={item.to}
        to={item.to}
        end={item.to === '/' || item.to.startsWith('/teams/')}
        onClick={onNavigate}
        className={({ isActive }) =>
          `sidebar-nav-item flex min-h-10 items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60${isActive ? ' active' : ''}`
        }
      >
        <item.icon className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="truncate">{item.label}</span>
        {item.badge !== undefined && (
          <span className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white">
            {item.badge}
          </span>
        )}
      </NavLink>
    ))
  }

  const userPanel = (
    <div className="shrink-0 border-t sidebar-divider px-4 py-4">
      <div className="flex items-center gap-3">
        <div className="sidebar-avatar flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-sm font-semibold">
          {userInitials}
        </div>
        <div className="min-w-0 flex-1">
          <p className="sidebar-user-name truncate text-sm font-semibold">
            {displayName}
          </p>
          <ContextSwitcher
            memberships={workMemberships}
            currentMembershipId={currentMembership?.id}
            onSwitch={switchContext}
            adminHref={hasOrgAdminAccess && workMemberships.length > 0 ? '/admin' : undefined}
            platformHref={SAAS_ENABLED && hasRole('super_admin') ? '/admin/platform-users' : undefined}
            organizationId={orgId}
          />
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2">
        <Link
          to="/preferences"
          className="sidebar-footer-link"
          title={t('common.settings')}
          aria-label={t('common.settings')}
        >
          <Settings className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{t('common.settings')}</span>
        </Link>
        <button
          type="button"
          onClick={logout}
          className="sidebar-footer-link"
        >
          <LogOut className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{t('common.signOut')}</span>
        </button>
        <div className="ml-auto flex items-center gap-3">
          {canSendFeedback && (
            <button
              type="button"
              onClick={() => setShowFeedback(true)}
              className="sidebar-footer-link"
              title={t('feedback.open')}
              aria-label={t('feedback.open')}
              aria-haspopup="dialog"
            >
              <MessageSquarePlus className="h-4 w-4 shrink-0" aria-hidden="true" />
            </button>
          )}
          <button
            type="button"
            onClick={() => setShowHelp(true)}
            className="sidebar-footer-link"
            title={t('onboarding.helpTitle')}
            aria-label={t('onboarding.helpTitle')}
          >
            <HelpCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  )

  return (
    <div className="app-shell flex h-screen overflow-hidden bg-muted">
      <aside className="sidebar-themed hidden h-screen w-64 shrink-0 flex-col border-r transition-colors duration-200 lg:flex">
        <div className="flex h-16 items-center justify-center border-b sidebar-divider">
          <span className="sidebar-logo-text text-xl font-bold tracking-tight font-mono">Gabai</span>
        </div>

        <nav className="min-h-0 flex-1 space-y-0.5 overflow-y-auto p-3" aria-label={t('nav.mainNavigation')}>
          {renderNavItems()}
        </nav>

        {userPanel}
      </aside>

      {isMobileNavOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-black/45"
            aria-label={t('nav.closeMenu')}
            onClick={() => setIsMobileNavOpen(false)}
          />
          <aside
            id="mobile-navigation"
            role="dialog"
            aria-modal="true"
            aria-label={t('nav.mainNavigation')}
            className="sidebar-themed absolute inset-y-0 left-0 flex w-[min(22rem,calc(100vw-3rem))] flex-col border-r shadow-xl"
          >
            <div className="flex h-16 items-center justify-between border-b sidebar-divider px-4">
              <span className="sidebar-logo-text text-lg font-bold tracking-tight font-mono">Gabai</span>
              <button
                type="button"
                className="sidebar-icon-btn"
                aria-label={t('nav.closeMenu')}
                onClick={() => setIsMobileNavOpen(false)}
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
            <nav className="min-h-0 flex-1 space-y-0.5 overflow-y-auto p-3" aria-label={t('nav.mainNavigation')}>
              {renderNavItems(() => setIsMobileNavOpen(false))}
            </nav>
            {userPanel}
          </aside>
        </div>
      )}

      <main className="relative h-screen min-w-0 flex-1 overflow-y-auto bg-grid">
        <header className="sticky top-0 z-40 flex h-14 items-center justify-between border-b border-border bg-card/95 px-4 backdrop-blur lg:hidden">
          <button
            type="button"
            className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-input bg-background text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
            aria-label={t('nav.openMenu')}
            aria-controls="mobile-navigation"
            aria-expanded={isMobileNavOpen}
            onClick={() => setIsMobileNavOpen(true)}
          >
            <Menu className="h-5 w-5" aria-hidden="true" />
          </button>
          <div className="min-w-0 px-3 text-center">
            <p className="truncate text-sm font-semibold text-foreground">
              {primaryContext?.productTeamName
                ?? (primaryContext?.entityType === 'UNIT' ? primaryContext.unit?.name : null)
                ?? primaryContext?.valueStreamName
                ?? primaryContext?.divisionName
                ?? primaryContext?.organizationName
                ?? t('nav.commandCenter')}
            </p>
            <p className="truncate text-xs text-muted-foreground">{primaryContext?.role ?? user?.email}</p>
          </div>
          <div className="flex items-center gap-2">
            {canSendFeedback && (
              <button
                type="button"
                className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-input bg-background text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
                aria-label={t('feedback.open')}
                aria-haspopup="dialog"
                onClick={() => setShowFeedback(true)}
              >
                <MessageSquarePlus className="h-5 w-5" aria-hidden="true" />
              </button>
            )}
            <Link
              to="/mobile"
              className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-input bg-background text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
              aria-label={t('nav.mobileCheckIn')}
            >
              <Smartphone className="h-5 w-5" aria-hidden="true" />
            </Link>
          </div>
        </header>

        <div className="app-top-glow pointer-events-none absolute inset-x-0 top-0 h-48 bg-gradient-to-b from-primary/5 to-transparent" />
        <div className="relative block px-4 pt-4 md:hidden">
          <Link
            to="/mobile"
            className="flex items-center justify-between rounded-lg border border-primary/20 bg-primary/10 px-4 py-2.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
          >
            <div className="flex items-center gap-2">
              <Smartphone className="h-4 w-4 text-primary" aria-hidden="true" />
              <span className="text-sm text-foreground">{t('mobile.mobileBanner')}</span>
            </div>
            <div className="flex items-center gap-1 text-sm font-medium text-primary">
              {t('mobile.mobileViewLink')}
              <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
            </div>
          </Link>
        </div>
        <div className="relative animate-fade-in p-4 sm:p-6 lg:p-8">
          <FolioStrip />
          <Outlet />
        </div>
      </main>

      <RoleHelpPanel open={showHelp} onClose={() => setShowHelp(false)} />
      {canSendFeedback && FeedbackDialog && <FeedbackDialog open={showFeedback} onOpenChange={setShowFeedback} />}
    </div>
  )
}
